// deviceList.js
import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import Spinner from '../../util/spinner-shim'
import { useParams, useNavigate, useLocation } from 'react-router-dom'

import { injectIntl } from 'react-intl'
import AppDispatcher from '../../dispatchers/AppDispatcher'
import AppStore from '../../stores/AppStore'
import withAppStore from '../../util/withAppStore'

// components
import NavigationBar from '../partials/NavigationBar'
import Footer from '../partials/Footer'
import GridList from '../partials/GridList'
import ModemReplacementWarning from '../partials/SettingPageComponents/ModemReplacementComponents/PopUps/MultiReplacementPopUp'
import { CapitalButton } from '../partials/Navigation/Buttons'

import styles from '../../../css/devicePage.module.css'

function DeviceList (props) {
  const { customerID } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const [state, setState] = React.useState({
    selectModeR1: false,
    selectModeR2: false,
    selectModeR3: false,
    selectModeWM: false,
    selectModePump: false,
    selectModeValve: false,
    selectAll: false,
    deviceType: '',
    multipleDevices: true,
    hasPump: false,
    hasValve: false,
    hasWM: false,
    hasOther: false,
    enableRpm: false,
    deviceFiltered: false,
    selectedState: {},
  })

  // Extract query parameters from location
  const searchParams = new URLSearchParams(location.search)
  const deviceGroup = searchParams.get('deviceGroup') || null
  const groupID = searchParams.get('groupID') || null
  const isActive = searchParams.get('isActive')
  const deviceGroupName = searchParams.get('deviceGroupName') || null

  const backToHome = () => {
    dispatch('unload-group-devices')

    if (isActive === 'false' && !groupID) {
      navigate('/settings')
      return
    }
    navigate('/')
  }

  const backToFilter = () => {
    const { selectModeR1, selectModeR2, selectModeR3 } = state
    if (selectModeR1 || selectModeR2 || selectModeR3) {
      setState((prevState) => ({
        ...prevState,
        selectModeR2: false,
        selectModeR1: false,
        selectModeR3: false,
        selectedState: {},
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        deviceType: '',
        deviceFiltered: false,
        selectModeR2: false,
        selectModeR1: false,
        selectModeR3: false,
        selectedState: {},
      }))
    }
    dispatch('selected-devices-remove-all')
  }

  const toTimeSet = () => {
    navigate(`/${customerID}/settings/timeSet/new`)
  }

  const toTimeblocks = () => {
    navigate(`/${customerID}/settings/timeblocks`)
  }

  const toTempSet = () => {
    navigate(`/${customerID}/settings/tempSet`)
  }

  const toRpmTarget = () => {
    navigate(`/${customerID}/settings/rpmTarget`)
  }

  const toChangeAutostartSettings = () => {
    navigate(`/${customerID}/settings/autostart`)
  }

  const dispatch = (action) => AppDispatcher.dispatch({ action })

  const getNewData = () => {
    const action = {
      action: 'get-devices',
      deviceGroup,
      customerID,
      groupID,
      // only show inactive devices when explicitly requested (Settings > Inactive Devices)
      isActive: isActive || 'true',
    }
    AppDispatcher.dispatch(action)
  }

  React.useEffect(() => {
    dispatch('unload-metric')
    dispatch('unload-device-status')
    const pollInterval = props.data?.pollInterval || 5000
    const interval = setInterval(getNewData, pollInterval)

    AppDispatcher.dispatch({
      action: 'save-history-list',
      path: `${location.pathname}${location.search}`,
    })

    return () => clearInterval(interval)
  }, [])

  React.useEffect(() => {
    getNewData()
  }, [])

  // Static getDerivedStateFromProps equivalent
  React.useEffect(() => {
    const {
      data: {
        groupDevices: {
          ready, devices,
        } = {},
      } = {},
    } = props

    if (ready && devices) {
      let {
        deviceType,
        hasPump,
        hasValve,
        hasWM,
        hasOther,
        enableRpm = false,
      } = state

      devices.forEach((device) => {
        if (
          device.interface_versioned &&
          device.interface_versioned.data &&
          device.interface_versioned.data.primaryPage.settingTypes
        ) {
          if (device.interface_versioned.data.primaryPage.settingTypes.includes('rpmTarget')) enableRpm = true
        } else if (
          device.interface &&
          device.interface.primaryPage.settingTypes
        ) {
          if (device.interface.primaryPage.settingTypes.includes('rpmTarget')) enableRpm = true
        }
        if (device.interface_versioned) {
          if (device.interface_versioned.type === 'pump') {
            hasPump = true
          } else if (device.interface_versioned.type === 'valve') {
            hasValve = true
          } else if (device.interface_versioned.type === 'windMachine') {
            hasWM = true
          } else {
            hasOther = true
          }
        }
      })

      if (hasWM && !hasValve && !hasPump && !hasOther) {
        deviceType = 'windMachine'
      } else if (hasValve && !hasPump && !hasWM && !hasOther) {
        deviceType = 'valve'
      }

      setState((prevState) => ({
        ...prevState,
        deviceType,
        hasPump,
        hasValve,
        hasWM,
        hasOther,
        enableRpm,
      }))
    }
  }, [props.data.groupDevices?.ready, props.data.groupDevices?.devices])

  const clickSelectAll = () => {
    const {
      selectAll,
      selectedState,
      selectModeR1,
      selectModeR2,
      selectModeR3,
      deviceType,
    } = state
    const allNotSelected = Object.keys(selectedState).every((k) => !selectedState[k])
    const anySelected = Object.keys(selectedState).some((k) => selectedState[k])

    let nextSelectAll
    if (allNotSelected && selectAll === true) {
      nextSelectAll = true
    } else if (anySelected) {
      nextSelectAll = false
    } else {
      nextSelectAll = !selectAll
    }
    const devices = Array.isArray(props.data?.groupDevices?.devices)
      ? props.data.groupDevices.devices
      : []

    if (!nextSelectAll) {
      AppDispatcher.dispatch({
        action: 'selected-devices-remove-all',
      })
      setState((prevState) => ({
        ...prevState,
        selectAll: nextSelectAll,
        selectedState: {},
      }))
      return
    }

    const deviceHasRpmTarget = (device) => {
      if (
        device.interface_versioned &&
        device.interface_versioned.data &&
        device.interface_versioned.data.primaryPage &&
        device.interface_versioned.data.primaryPage.settingTypes
      ) {
        return device.interface_versioned.data.primaryPage.settingTypes.includes('rpmTarget')
      }
      if (
        device.interface &&
        device.interface.primaryPage &&
        device.interface.primaryPage.settingTypes
      ) {
        return device.interface.primaryPage.settingTypes.includes('rpmTarget')
      }
      return false
    }

    const nextSelectedState = {}
    const selectedIds = []

    devices.forEach((device) => {
      if ((selectModeR3 && deviceHasRpmTarget(device)) || selectModeR1 || selectModeR2) {
        const previouslySelected = Boolean(selectedState[device.id])
        const isSelected = !previouslySelected

        nextSelectedState[device.id] = isSelected

        if (isSelected && device.interface_versioned && device.interface_versioned.type === deviceType) {
          selectedIds.push(device.id)
        }
      }
    })

    AppDispatcher.dispatch({
      action: 'select-multiple-devices',
      ids: selectedIds,
    })

    setState((prevState) => ({
      ...prevState,
      selectAll: nextSelectAll,
      selectedState: nextSelectedState,
    }))
  }

  const selectModeR1Toggle = () => {
    const { selectModeR1 } = state

    setState((prevState) => ({
      ...prevState,
      selectModeR1: !selectModeR1,
      selectModeR2: false,
      selectModeR3: false,
      selectedState: {},
    }))

    if (!selectModeR1) {
      dispatch('selected-devices-remove-all')
    }
  }

  const selectModeR2Toggle = () => {
    const { selectModeR2 } = state
    setState((prevState) => ({
      ...prevState,
      selectModeR2: !selectModeR2,
      selectModeR1: false,
      selectModeR3: false,
      selectedState: {},
    }))
  }

  const selectModeR3Toggle = () => {
    const { selectModeR3 } = state
    setState((prevState) => ({
      ...prevState,
      selectModeR3: !selectModeR3,
      selectModeR1: false,
      selectModeR2: false,
      selectedState: {},
    }))

    if (!selectModeR3) {
      dispatch('selected-devices-remove-all')
    }
  }

  const selectDeviceTypeWM = () => {
    const { selectModeWM } = state

    setState((prevState) => ({
      ...prevState,
      selectModeWM: !selectModeWM,
      selectModePump: false,
      selectModeValve: false,
      selectedState: {},
      deviceType: 'windMachine',
      deviceFiltered: true,
    }))

    if (!selectModeWM) {
      dispatch('selected-devices-remove-all')
    }
  }

  const selectDeviceTypePump = () => {
    const { selectModePump } = state

    setState((prevState) => ({
      ...prevState,
      selectModePump: !selectModePump,
      selectModeWM: false,
      selectModeValve: false,
      selectedState: {},
      deviceType: 'pump',
      deviceFiltered: true,
    }))

    if (!selectModePump) {
      dispatch('selected-devices-remove-all')
    }
  }

  const selectDeviceTypeValve = () => {
    const { selectModeValve } = state

    setState((prevState) => ({
      ...prevState,
      selectModeValve: !selectModeValve,
      selectModePump: false,
      selectModeWM: false,
      selectedState: {},
      deviceType: 'valve',
      deviceFiltered: true,
    }))

    if (!selectModeValve) {
      dispatch('selected-devices-remove-all')
    }
  }

  const subMenuClose = () => {
    const { selectModeR2 } = state
    setState((prevState) => ({
      ...prevState,
      selectModeR2: false,
      selectModeR1: false,
      selectModeR3: false,
    }))

    if (!selectModeR2) {
      dispatch('selected-devices-remove-all')
    }
  }

  const render = () => {
    const {
      auth: { logout },
      data,
      data: {
        deviceSettings: { devices: settingsDevices = [] } = {},
      } = {},
      intl: { formatMessage },
      pages: { DeviceList: title },
      route: { path: page },
    } = props

    const deviceData = data.groupDevices
    const { ready, devices } = deviceData || {}
    const {
      selectModeR1,
      selectModeR2,
      selectModeR3,
      selectModeWM,
      selectModePump,
      selectModeValve,
      selectAll,
      selectedState,
      deviceType,
      enableRpm,
      hasPump,
      hasWM,
      hasValve,
      hasOther,
      deviceFiltered,
      multipleDevices,
    } = state

    /*     let deviceGroupName = '';
    if (typeof AppStore.getCustomer() === 'object') {
      const { applications } = AppStore.getCustomer();
      if (Array.isArray(applications) && applications.length > 0) {
        const group = applications.find((item) => item.deviceGroup === deviceGroup);
        ({ name: deviceGroupName } = group || {});
      }
    } */

    let currentDevices = []

    // TODO: Remove this timeblocks ready stuff when we don't have the old scheduler
    let timeblocksCount = 0
    let timeblocksReady = false

    if (deviceData && ready && Array.isArray(devices)) {
      devices.forEach((device) => {
        if (device.interface_versioned) {
          if (device.interface_versioned.type === deviceType) {
            currentDevices.push(device)
            if (
              device.interface_versioned.data &&
              device.interface_versioned.data.other &&
              device.interface_versioned.data.other.schedule &&
              device.interface_versioned.data.other.schedule === 3
            ) {
              timeblocksCount += 1
            }
          }
        }
      })
      if (currentDevices.length === 0) {
        currentDevices = devices
      }
      if (currentDevices.length === timeblocksCount) {
        timeblocksReady = true
      }
    }
    let filterMenuIsActive = false

    const menuItems = []
    // TODO: Get user level from AppStore or props
    const userLevel = AppStore.getUser()?.permissions?.level || 0
    if (userLevel >= 10) {
      if (hasWM) {
        if (hasValve || hasPump || hasOther) {
          menuItems.push({
            icon: 'wm',
            clickFunc: selectDeviceTypeWM,
            active: selectModeWM,
          })
        }
        filterMenuIsActive = true
      }
      if (hasPump) {
        menuItems.push({
          icon: 'pump',
          clickFunc: selectDeviceTypePump,
          active: selectModePump,
        })
      }
      if (hasValve) {
        if (hasWM || hasPump || hasOther) {
          menuItems.push({
            icon: 'valve',
            clickFunc: selectDeviceTypeValve,
            active: selectModeValve,
          })
        }
        filterMenuIsActive = true
      }

      if (deviceType !== '') {
        menuItems.length = 0
      }
      if (
        deviceType === 'windMachine' ||
        deviceType === 'coldAirDrain' ||
        deviceType === 'pumpFrostWater'
      ) {
        if (enableRpm) {
          menuItems.push({
            icon: 'rpm',
            clickFunc: selectModeR3Toggle,
            active: selectModeR3,
          })
        }
        menuItems.push({
          icon: 'fa-thermometer-half',
          clickFunc: selectModeR1Toggle,
          active: selectModeR1,
        })
        menuItems.push({
          icon: 'fa-power-off',
          clickFunc: selectModeR2Toggle,
          active: selectModeR2,
        })
      } else if (deviceType === 'valve') {
        menuItems.push({
          icon: 'fa-calendar-check-o',
          clickFunc: selectModeR1Toggle,
          active: selectModeR1,
        })
      }
    }
    let tileSetInfo = 'default'
    if (selectModeR3) {
      tileSetInfo = 'rpm'
    }
    const gridList =
      deviceData && ready && Array.isArray(currentDevices)
        ? (
          <GridList
            devices={currentDevices}
            deviceType={deviceType}
            sort={data?.sort}
            selectMode={selectModeR1 || selectModeR2 || selectModeR3}
            tileInfo={tileSetInfo}
            selectedStateAll={selectedState}
          />
          )
        : (
          <div className='loading'>
            <Spinner name='double-bounce' />
          </div>
          )

    let multiFunctionButton
    let selectAllButton

    if (selectModeR1 && settingsDevices.length > 0) {
      switch (deviceType) {
        case 'windMachine':
        case 'coldAirDrain':
        case 'pumpFrostWater':
          multiFunctionButton = (
            <CapitalButton
              text={formatMessage({
                id: 'setting.tempSet',
                defaultMessage: 'TEMP SET',
                description: '',
              }).toUpperCase()}
              buttonFunction={toTempSet}
              hSize='h2'
              style={styles.deselectAllButton}
            />
          )
          break
        case 'valve':
          multiFunctionButton = (
            <CapitalButton
              text={formatMessage({
                id: 'setting.schedule',
                defaultMessage: 'SCHEDULE',
                description: '',
              }).toUpperCase()}
              buttonFunction={timeblocksReady ? toTimeblocks : toTimeSet}
              hSize='h2'
              style={styles.deselectAllButton}
            />
          )
          break
        default:
          break
      }
    } else if (selectModeR2 && settingsDevices.length > 0) {
      switch (deviceType) {
        case 'windMachine':
        case 'coldAirDrain':
        case 'pumpFrostWater':
          multiFunctionButton = (
            <CapitalButton
              text={formatMessage({
                id: 'setting.change',
                defaultMessage: 'CHANGE SETTINGS',
                description: '',
              }).toUpperCase()}
              buttonFunction={toChangeAutostartSettings}
              hSize='h2'
              style={styles.deselectAllButton}
            />
          )
          break
        default:
          break
      }
    } else if (selectModeR3 && settingsDevices.length > 0) {
      switch (deviceType) {
        case 'windMachine':
        case 'coldAirDrain':
        case 'pumpFrostWater':
          multiFunctionButton = (
            <CapitalButton
              text={formatMessage({
                id: 'setting.speed.target.label',
                defaultMessage: 'RPM Target',
                description: '',
              }).toUpperCase()}
              buttonFunction={toRpmTarget}
              hSize='h2'
              style={styles.deselectAllButton}
            />
          )
          break
        default:
          break
      }
    }

    if (selectModeR3 || selectModeR2 || selectModeR1) {
      const allNotSelected = Object.keys(selectedState).every((k) => !selectedState[k])
      const anySelected = Object.keys(selectedState).some((k) => selectedState[k])
      let id = 'setting.select.all'
      let message = 'Select All'
      let buttonStyles = styles.selectAllButton
      if ((selectAll && !allNotSelected) || anySelected) {
        id = 'setting.select.none'
        message = 'Deselect All'
        buttonStyles = styles.deselectAllButton
      } else if (allNotSelected) {
        id = 'setting.select.all'
        message = 'Select All'
        buttonStyles = styles.selectAllButton
      }

      selectAllButton = (

        <CapitalButton
          text={formatMessage({
            id,
            defaultMessage: message,
            description: '',
          }).toUpperCase()}
          buttonFunction={clickSelectAll}
          hSize='h5'
          style={`${buttonStyles} ${styles.selectAllButtonStyles}`}
        />
      )
    }

    return (
      <div className={styles.pageBottom}>
        <Helmet><title>{title}</title></Helmet>
        <NavigationBar
          backFunc={(deviceFiltered || selectModeR1 || selectModeR2 || selectModeR3) ? backToFilter : backToHome}
          logOut={() => logout(navigate)}
          menuItems={menuItems}
          name={deviceGroupName}
          page={page}
          subMenuClose={subMenuClose}
          filterMenuIsActive={filterMenuIsActive}
        />
        <ModemReplacementWarning
          devices={currentDevices}
          customerID={customerID}
          groupID={groupID}
          deviceGroupName={deviceGroupName}
          multipleDevices
        />
        <div className={styles.tileContainer}>
          {selectAllButton}
        </div>
        {gridList}
        <div className={styles.tileContainer}>{multiFunctionButton}</div>
        <Footer />
      </div>
    )
  }

  return render()
}

DeviceList.propTypes = {
  data: PropTypes.object,
  pages: PropTypes.object,
  route: PropTypes.object,
  auth: PropTypes.object,
  intl: PropTypes.object,
}

export default injectIntl(withAppStore(DeviceList))
